import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  dreamTeamAsPickLight,
  getEventDreamTeam,
  getEventDreamTeamFormation,
  getOverallDreamTeam,
  getOverallDreamTeamFormation,
} from "core-integration/src/store/dream-teams/reducers";
import {
  fetchEventDreamTeam,
  fetchOverallDreamTeam,
} from "core-integration/src/store/dream-teams/thunks";
import {
  getElementsById,
  getElementsEventDataById,
} from "core-integration/src/store/elements/reducers";
import {
  fetchEventLive,
  showElementSummary,
} from "core-integration/src/store/elements/thunks";
import { IPickLight } from "core-integration/src/store/entries/types";
import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import { getFixturesForEventById } from "core-integration/src/store/fixtures/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import React, { useEffect, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { integerDivide } from "../../utils/points";
import BoldLink from "../BoldLink";
import ButtonLink from "../ButtonLink";
import Fixtures from "../Fixtures";
import { ControlArrowEnd, ControlArrowStart } from "../icons/Arrows";
import { Main, Secondary, Wrapper } from "../Layout";
import LinkButton from "../LinkButton";
import Media from "../Media";
import {
  EventPager,
  PagerButton,
  PagerButtonNext,
  PagerHeading,
} from "../Pager";
import { NotFound } from "../Routes";
import Shirt from "../Shirt";
import SubHeading from "../SubHeading";
import TabPanel from "../tabs/TabPanel";
import Tabs from "../tabs/Tabs";
import Title from "../Title";
import DreamTeamTable from "./DreamTeamTable";
import ElementExplainDialog from "./ElementExplainDialog";
import PitchFormation from "./PitchFormation";
import valueForPlayerElement from "./valueForPlayedElement";

const DTScoreboard = styled.div`
  display: flex;
  background-color: #e2e2e2;
`;

const DTScoreboardPanel = styled.div`
  flex: 1;
  margin: ${({ theme }) => theme.space[2]};
  padding: 0 ${({ theme }) => theme.space[2]};
  background-color: white;
  text-align: center;
`;

const DTScoreboardHeading = styled.h3`
  margin-bottom: ${({ theme }) => theme.space[1]};
  padding-top: ${({ theme }) => theme.space[2]};
  font-size: ${({ theme }) => theme.fontSizes[4]};
`;

const DTScoreboardValue = styled.div`
  position: relative;
  font-size: 4rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    padding-bottom: 0.3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding-top: 0.6rem;
    padding-bottom: 1rem;
  }
`;

const TopPlayer = styled.div`
  display: inline-block;
`;

const TopPlayerData = styled.div`
  text-align: left;
`;

type Props = WithTranslation;

const DreamTeam: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [elementForMenu, setElementForMenu] = useState<number>(0);
  const { eventId } = useParams();

  const data = useSelector((state: RootState) =>
    eventId
      ? getEventDreamTeam(state, Number(eventId))
      : getOverallDreamTeam(state),
  );

  const elementsById = useSelector(getElementsById);
  const elementsDataById = useSelector((state: RootState) =>
    getElementsEventDataById(state, Number(eventId)),
  );
  const event = useSelector(getEventsById)[Number(eventId)];
  const fixturesById = useSelector((state: RootState) =>
    getFixturesForEventById(state, Number(eventId)),
  );
  const formation = useSelector((state: RootState) =>
    eventId
      ? getEventDreamTeamFormation(state, Number(eventId))
      : getOverallDreamTeamFormation(state),
  );
  const now = useSelector(getCurrentEvent);
  const teamsById = useSelector(getTeamsById);

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const handleShowMenuForElement = (element: number) => {
    setElementForMenu(element);
  };

  const handleHideMenuForElement = () => {
    setElementForMenu(0);
  };

  useEffect(() => {
    if (event) {
      dispatch(fetchEventDreamTeam(event.id));
      dispatch(fetchEventLive(event.id));
    } else {
      dispatch(fetchOverallDreamTeam());
    }
  }, [dispatch, event]);

  if (!now) {
    return <NotFound />;
  }
  if (!data) {
    return null;
  }
  const picks = dreamTeamAsPickLight(data.team);
  const topPlayer = elementsById[data.top_player.id];

  // For the overall team we just show the standard element dialog as there
  // is no explain
  if (elementForMenu && !event) {
    showElementDialog(elementForMenu);
  }

  // Create a new function on each render as data could have changed and
  // need to ensure a render of connected subcomponents
  const renderPickValue = event
    ? valueForPlayerElement({
        elementsById,
        elementsDataById,
        fixturesById,
        teamsById,
      })
    : (pick: IPickLight) => {
        const elementData = data.team.filter((e) => e.element === pick.element);
        return elementData.length
          ? integerDivide(elementData[0].points, 10)
          : null;
      };
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{t("dreamTeam.dt", "Team of the Week")}</Title>
        </Box>
        {/* ROUND PAGER */}
        {event ? (
          <Box mb={4}>
            <PagerHeading>{event.name}</PagerHeading>
            {(event.id > 1 || event.id < now.id) && (
              <EventPager>
                {event.id > 1 && (
                  <PagerButton>
                    <ButtonLink
                      to={`/dream-team/${event.id - 1}`}
                      variant="secondary"
                    >
                      <ControlArrowStart />
                      {t("dreamTeam.prev", "Previous")}
                    </ButtonLink>
                  </PagerButton>
                )}
                {event.id < now.id && (
                  <PagerButtonNext>
                    <ButtonLink
                      to={`/dream-team/${event.id + 1}`}
                      variant="secondary"
                    >
                      {t("dreamTeam.next", "Next")}
                      <ControlArrowEnd />
                    </ButtonLink>
                  </PagerButtonNext>
                )}
              </EventPager>
            )}
          </Box>
        ) : (
          <Box mx={2}>
            <SubHeading>{t("dreamTeam.overall", "Overall")}</SubHeading>
          </Box>
        )}
        {/* SCOREBOARD */}

        <Box mb={4}>
          <DTScoreboard>
            <DTScoreboardPanel>
              <DTScoreboardHeading>
                {t("dreamTeam.topPlayer", "Top Player")}
              </DTScoreboardHeading>
              <Box mb={1}>
                <LinkButton onClick={() => showElementDialog(topPlayer.id)}>
                  {topPlayer.web_name}
                  <ControlArrowEnd />
                </LinkButton>
              </Box>
              <Box mb={1}>
                <TopPlayer>
                  <Media
                    img={<Shirt elementId={topPlayer.id} />}
                    centred={true}
                  >
                    <TopPlayerData>
                      <div>{teamsById[topPlayer.team].short_name}</div>
                      <div>
                        {integerDivide(data.top_player.points, 10)}{" "}
                        {t("dreamTeam.points", "Points")}
                      </div>
                    </TopPlayerData>
                  </Media>
                </TopPlayer>
              </Box>
            </DTScoreboardPanel>
            <DTScoreboardPanel>
              <DTScoreboardHeading>
                {t("dreamTeam.totalPoints", "Total Points")}
              </DTScoreboardHeading>
              <DTScoreboardValue>
                {integerDivide(
                  data.team.reduce((total, e) => total + e.points, 0),
                  10,
                )}
              </DTScoreboardValue>
              {event ? (
                <BoldLink to="/dream-team/">
                  {t("dreamTeam.overall", "Overall")}
                </BoldLink>
              ) : (
                <BoldLink to={`/dream-team/${now.id}`}>
                  {t("dreamTeam.round", "Round")}
                </BoldLink>
              )}
            </DTScoreboardPanel>
          </DTScoreboard>
        </Box>
        {/* TEAM */}
        <Box bg="primary" pt={4}>
          <Box mx={2} mb={2}>
            <Tabs centered>
              <TabPanel
                label={t("dreamTeam.pitchView", "Court View")}
                link="pitch"
              >
                <Box mx={-2} pt={4}>
                  <PitchFormation
                    chipName={""}
                    formation={formation}
                    picks={picks}
                    renderDreamTeam={() => null}
                    renderElementMenu={handleShowMenuForElement}
                    renderPickValue={renderPickValue}
                  />
                </Box>
              </TabPanel>
              <TabPanel
                label={t("dreamTeam.listView", "List View")}
                link="list"
              >
                <Box my={4} bg="white">
                  <DreamTeamTable
                    elements={data.team.map((e) => e.element)}
                    renderElementMenu={handleShowMenuForElement}
                    dataById={elementsDataById}
                  />
                </Box>
              </TabPanel>
            </Tabs>
          </Box>
        </Box>
        {event && elementForMenu ? (
          <ElementExplainDialog
            elementId={elementForMenu}
            eventId={event.id}
            closeDialog={handleHideMenuForElement}
          />
        ) : null}
        {event && <Fixtures eventId={event.id} />}
      </Main>
      <Secondary>
        <span>&nbsp;</span>
      </Secondary>
    </Wrapper>
  );
};

export default withTranslation()(DreamTeam);
